import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Fuel, 
  DollarSign, 
  Droplets, 
  AlertTriangle, 
  Check, 
  X, 
  Clock,
  Gauge,
  Car,
  CreditCard,
  Banknote,
  RotateCcw,
  Volume2,
  VolumeX,
  Settings,
  TrendingUp,
  Database,
  Store,
  ShoppingCart,
  Plus,
  Minus,
  Trash2,
  Search,
  Coffee,
  Cigarette,
  Wine,
  Cookie,
  Sandwich,
  Package,
  Receipt,
  Printer,
  ChevronLeft,
  ChevronRight,
  ToggleLeft,
  ToggleRight,
  LayoutGrid,
  Maximize2,
  Minimize2,
  ArrowLeft,
  Play
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';

// Pump Status Types
const PUMP_STATUS = {
  IDLE: 'idle',
  AUTHORIZED: 'authorized',
  PUMPING: 'pumping',
  COMPLETE: 'complete',
  OFFLINE: 'offline',
  CALL: 'call'
};

// Fuel Grades
const FUEL_GRADES = [
  { id: 'regular', name: 'Regular', octane: '87', color: 'bg-green-500', price: 3.299 },
  { id: 'plus', name: 'Plus', octane: '89', color: 'bg-blue-500', price: 3.599 },
  { id: 'premium', name: 'Premium', octane: '93', color: 'bg-purple-500', price: 3.999 },
  { id: 'diesel', name: 'Diesel', octane: 'D2', color: 'bg-yellow-500', price: 3.799 },
];

// C-Store Categories
const STORE_CATEGORIES = [
  { id: 'drinks', name: 'Drinks', icon: Coffee },
  { id: 'snacks', name: 'Snacks', icon: Cookie },
  { id: 'tobacco', name: 'Tobacco', icon: Cigarette },
  { id: 'food', name: 'Food', icon: Sandwich },
  { id: 'alcohol', name: 'Alcohol', icon: Wine },
  { id: 'other', name: 'Other', icon: Package },
];

// Sample C-Store Products
const STORE_PRODUCTS = [
  { id: 1, name: 'Coffee Large', price: 2.49, category: 'drinks', icon: '☕' },
  { id: 2, name: 'Red Bull', price: 3.99, category: 'drinks', icon: '🥤' },
  { id: 3, name: 'Monster Energy', price: 3.49, category: 'drinks', icon: '🥤' },
  { id: 4, name: 'Coca-Cola 20oz', price: 2.29, category: 'drinks', icon: '🥤' },
  { id: 5, name: 'Water Bottle', price: 1.99, category: 'drinks', icon: '💧' },
  { id: 6, name: 'Gatorade', price: 2.49, category: 'drinks', icon: '🥤' },
  { id: 7, name: 'Doritos', price: 2.99, category: 'snacks', icon: '🔺' },
  { id: 8, name: 'Lay\'s Chips', price: 2.49, category: 'snacks', icon: '🥔' },
  { id: 9, name: 'Snickers', price: 1.89, category: 'snacks', icon: '🍫' },
  { id: 10, name: 'M&Ms', price: 1.99, category: 'snacks', icon: '🍬' },
  { id: 11, name: 'Beef Jerky', price: 6.99, category: 'snacks', icon: '🥩' },
  { id: 12, name: 'Peanuts', price: 2.49, category: 'snacks', icon: '🥜' },
  { id: 13, name: 'Marlboro Red', price: 9.99, category: 'tobacco', icon: '🚬', ageRestricted: true },
  { id: 14, name: 'Camel Blue', price: 8.99, category: 'tobacco', icon: '🚬', ageRestricted: true },
  { id: 15, name: 'Juul Pods', price: 15.99, category: 'tobacco', icon: '💨', ageRestricted: true },
  { id: 16, name: 'Disposable Vape', price: 12.99, category: 'tobacco', icon: '💨', ageRestricted: true },
  { id: 17, name: 'Hot Dog', price: 2.49, category: 'food', icon: '🌭' },
  { id: 18, name: 'Pizza Slice', price: 3.49, category: 'food', icon: '🍕' },
  { id: 19, name: 'Breakfast Sandwich', price: 4.99, category: 'food', icon: '🥪' },
  { id: 20, name: 'Nachos', price: 4.49, category: 'food', icon: '🧀' },
  { id: 21, name: 'Bud Light 6pk', price: 9.99, category: 'alcohol', icon: '🍺', ageRestricted: true },
  { id: 22, name: 'White Claw', price: 12.99, category: 'alcohol', icon: '🥫', ageRestricted: true },
  { id: 23, name: 'Lottery Ticket', price: 2.00, category: 'other', icon: '🎫' },
  { id: 24, name: 'Phone Charger', price: 14.99, category: 'other', icon: '🔌' },
];

// Generate mock pumps
const generateMockPumps = (count = 8) => {
  return Array.from({ length: count }, (_, i) => ({
    id: i + 1,
    status: i === 2 ? PUMP_STATUS.PUMPING : i === 5 ? PUMP_STATUS.COMPLETE : i === 7 ? PUMP_STATUS.OFFLINE : PUMP_STATUS.IDLE,
    currentAmount: i === 2 ? 12.45 : i === 5 ? 35.00 : 0,
    currentGallons: i === 2 ? 3.78 : i === 5 ? 10.61 : 0,
    presetAmount: i === 2 ? 25.00 : i === 5 ? 35.00 : null,
    selectedGrade: i === 2 ? 'regular' : i === 5 ? 'premium' : null,
  }));
};

// Mini Pump Card for combined view
const MiniPumpCard = ({ pump, onSelect, isSelected }) => {
  const getStatusColor = () => {
    switch (pump.status) {
      case PUMP_STATUS.IDLE: return 'bg-slate-700 border-slate-600';
      case PUMP_STATUS.AUTHORIZED: return 'bg-yellow-500/20 border-yellow-500';
      case PUMP_STATUS.PUMPING: return 'bg-emerald-500/20 border-emerald-500 animate-pulse';
      case PUMP_STATUS.COMPLETE: return 'bg-blue-500/20 border-blue-500';
      case PUMP_STATUS.OFFLINE: return 'bg-red-500/20 border-red-500 opacity-50';
      default: return 'bg-slate-700 border-slate-600';
    }
  };

  return (
    <motion.div
      whileTap={{ scale: 0.95 }}
      onClick={() => onSelect(pump)}
      className={`relative p-2 rounded-lg border cursor-pointer transition-all ${getStatusColor()} ${isSelected ? 'ring-2 ring-emerald-400' : ''}`}
    >
      <div className="absolute -top-1.5 -left-1.5 h-5 w-5 rounded-full bg-slate-800 border border-slate-600 flex items-center justify-center">
        <span className="text-[10px] font-bold text-white">{pump.id}</span>
      </div>
      <div className="text-center pt-1">
        <p className="text-sm font-bold text-white font-mono">${pump.currentAmount.toFixed(2)}</p>
        <p className="text-[10px] text-slate-400">{pump.currentGallons.toFixed(2)}g</p>
      </div>
    </motion.div>
  );
};

// Prepay Dialog
const PrepayDialog = ({ open, onClose, pump, onAuthorize }) => {
  const [amount, setAmount] = useState('');
  const [selectedGrade, setSelectedGrade] = useState('regular');

  const quickAmounts = [10, 20, 25, 30, 40, 50];

  const handleAuthorize = () => {
    onAuthorize({
      pumpId: pump?.id,
      amount: parseFloat(amount),
      grade: selectedGrade,
    });
    onClose();
    setAmount('');
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Fuel className="h-5 w-5 text-emerald-400" />
            Prepay - Pump {pump?.id}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <div className="grid grid-cols-3 gap-2">
            {quickAmounts.map((amt) => (
              <Button
                key={amt}
                size="sm"
                variant={amount === amt.toString() ? 'default' : 'outline'}
                className={amount === amt.toString() ? 'bg-emerald-600' : 'border-slate-600'}
                onClick={() => setAmount(amt.toString())}
              >
                ${amt}
              </Button>
            ))}
          </div>
          <div className="relative">
            <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Custom amount"
              className="pl-9 bg-slate-800 border-slate-600"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            {FUEL_GRADES.map((grade) => (
              <Button
                key={grade.id}
                size="sm"
                variant="outline"
                className={`text-xs ${selectedGrade === grade.id ? 'border-emerald-500 bg-emerald-500/10' : 'border-slate-600'}`}
                onClick={() => setSelectedGrade(grade.id)}
              >
                <span className={`h-2 w-2 rounded-full ${grade.color} mr-1`}></span>
                {grade.name} ${grade.price.toFixed(2)}
              </Button>
            ))}
          </div>
          <Button 
            className="w-full bg-emerald-600 hover:bg-emerald-700"
            onClick={handleAuthorize}
            disabled={!amount || parseFloat(amount) <= 0}
          >
            <Check className="h-4 w-4 mr-2" />
            Authorize Pump {pump?.id}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Age Verification Dialog
const AgeVerificationDialog = ({ open, onClose, onConfirm, product }) => {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-yellow-400">
            <AlertTriangle className="h-5 w-5" />
            Age Verification Required
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <p className="text-slate-300">
            <strong>{product?.name}</strong> is age-restricted. Customer must be 21+ years old.
          </p>
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
            <p className="text-sm text-yellow-200">Please verify customer's ID before proceeding.</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Button variant="outline" className="border-slate-600" onClick={onClose}>
              Cancel
            </Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={onConfirm}>
              ID Verified - Add Item
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Main Combined Gas Station + C-Store POS
const GasStationCStorePOS = () => {
  const navigate = useNavigate();
  
  // Settings
  const [convenienceStoreEnabled, setConvenienceStoreEnabled] = useState(true);
  const [expandedView, setExpandedView] = useState('balanced'); // 'pumps', 'store', 'balanced'
  
  // Pump state
  const [pumps, setPumps] = useState(generateMockPumps(8));
  const [selectedPump, setSelectedPump] = useState(null);
  const [showPrepayDialog, setShowPrepayDialog] = useState(false);
  
  // Store state
  const [selectedCategory, setSelectedCategory] = useState('drinks');
  const [searchQuery, setSearchQuery] = useState('');
  const [cart, setCart] = useState([]);
  const [pendingAgeVerification, setPendingAgeVerification] = useState(null);
  
  // Filter products
  const filteredProducts = STORE_PRODUCTS.filter(p => {
    const matchesCategory = selectedCategory === 'all' || p.category === selectedCategory;
    const matchesSearch = p.name.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // Simulate pumping
  useEffect(() => {
    const interval = setInterval(() => {
      setPumps(prev => prev.map(pump => {
        if (pump.status === PUMP_STATUS.PUMPING && pump.presetAmount) {
          const newAmount = Math.min(pump.currentAmount + 0.35, pump.presetAmount);
          const gradePrice = FUEL_GRADES.find(g => g.id === pump.selectedGrade)?.price || 3.299;
          if (newAmount >= pump.presetAmount) {
            return { ...pump, currentAmount: pump.presetAmount, currentGallons: pump.presetAmount / gradePrice, status: PUMP_STATUS.COMPLETE };
          }
          return { ...pump, currentAmount: newAmount, currentGallons: newAmount / gradePrice };
        }
        return pump;
      }));
    }, 500);
    return () => clearInterval(interval);
  }, []);

  // Pump handlers
  const handleSelectPump = (pump) => {
    setSelectedPump(pump);
    if (pump.status === PUMP_STATUS.IDLE) {
      setShowPrepayDialog(true);
    } else if (pump.status === PUMP_STATUS.COMPLETE) {
      // Add fuel to cart
      const gradeInfo = FUEL_GRADES.find(g => g.id === pump.selectedGrade);
      const fuelItem = {
        id: `fuel-pump-${pump.id}-${Date.now()}`,
        name: `Pump ${pump.id} - ${gradeInfo?.name || 'Fuel'}`,
        price: pump.currentAmount,
        quantity: 1,
        isFuel: true,
        gallons: pump.currentGallons,
        pumpId: pump.id
      };
      setCart(prev => [...prev, fuelItem]);
      // Reset pump
      setPumps(prev => prev.map(p => 
        p.id === pump.id 
          ? { ...p, status: PUMP_STATUS.IDLE, currentAmount: 0, currentGallons: 0, presetAmount: null, selectedGrade: null }
          : p
      ));
    }
  };

  const handleAuthorize = (data) => {
    setPumps(prev => prev.map(pump => 
      pump.id === data.pumpId 
        ? { ...pump, status: PUMP_STATUS.AUTHORIZED, presetAmount: data.amount, selectedGrade: data.grade, currentAmount: 0, currentGallons: 0 }
        : pump
    ));
    setTimeout(() => {
      setPumps(prev => prev.map(pump => 
        pump.id === data.pumpId && pump.status === PUMP_STATUS.AUTHORIZED
          ? { ...pump, status: PUMP_STATUS.PUMPING }
          : pump
      ));
    }, 1500);
  };

  // Store handlers
  const handleAddToCart = (product) => {
    if (product.ageRestricted) {
      setPendingAgeVerification(product);
      return;
    }
    addProductToCart(product);
  };

  const addProductToCart = (product) => {
    setCart(prev => {
      const existing = prev.find(item => item.id === product.id && !item.isFuel);
      if (existing) {
        return prev.map(item => 
          item.id === product.id && !item.isFuel
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prev, { ...product, quantity: 1 }];
    });
  };

  const handleAgeVerified = () => {
    if (pendingAgeVerification) {
      addProductToCart(pendingAgeVerification);
      setPendingAgeVerification(null);
    }
  };

  const updateCartQuantity = (itemId, delta) => {
    setCart(prev => prev.map(item => {
      if (item.id === itemId) {
        const newQty = item.quantity + delta;
        return newQty > 0 ? { ...item, quantity: newQty } : item;
      }
      return item;
    }).filter(item => item.quantity > 0));
  };

  const removeFromCart = (itemId) => {
    setCart(prev => prev.filter(item => item.id !== itemId));
  };

  // Totals
  const cartTotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const taxRate = 0.0825; // 8.25% Texas tax example
  const taxAmount = cartTotal * taxRate;
  const grandTotal = cartTotal + taxAmount;

  // Stats
  const activePumps = pumps.filter(p => p.status === PUMP_STATUS.PUMPING || p.status === PUMP_STATUS.AUTHORIZED).length;
  const completedPumps = pumps.filter(p => p.status === PUMP_STATUS.COMPLETE).length;

  return (
    <div className="h-screen bg-slate-900 text-white flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-slate-800/50 border-b border-slate-700">
        <div className="flex items-center gap-3">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate('/pos')}
            className="text-slate-400 hover:text-white hover:bg-slate-700"
            data-testid="back-to-pos-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Standard POS
          </Button>
          <div className="h-8 w-px bg-slate-700" />
          <div className="h-10 w-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
            <Fuel className="h-5 w-5 text-emerald-400" />
          </div>
          <div>
            <h1 className="text-lg font-bold">Gas Station POS</h1>
            <p className="text-xs text-slate-400">{pumps.length} Pumps • {activePumps} Active</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Try Demo Button */}
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => navigate('/pos/gas-station-demo')}
            className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
            data-testid="try-demo-btn"
          >
            <Play className="h-4 w-4 mr-1" />
            Try Demo
          </Button>

          {/* C-Store Toggle */}
          <div className="flex items-center gap-2 bg-slate-800 rounded-lg px-3 py-1.5">
            <Store className="h-4 w-4 text-slate-400" />
            <span className="text-sm">C-Store</span>
            <button 
              onClick={() => setConvenienceStoreEnabled(!convenienceStoreEnabled)}
              className="ml-1"
            >
              {convenienceStoreEnabled ? (
                <ToggleRight className="h-6 w-6 text-emerald-400" />
              ) : (
                <ToggleLeft className="h-6 w-6 text-slate-500" />
              )}
            </button>
          </div>

          {/* View Mode */}
          {convenienceStoreEnabled && (
            <div className="flex items-center bg-slate-800 rounded-lg p-1">
              <button 
                onClick={() => setExpandedView('pumps')}
                className={`px-2 py-1 rounded text-xs ${expandedView === 'pumps' ? 'bg-emerald-600 text-white' : 'text-slate-400'}`}
              >
                Pumps
              </button>
              <button 
                onClick={() => setExpandedView('balanced')}
                className={`px-2 py-1 rounded text-xs ${expandedView === 'balanced' ? 'bg-emerald-600 text-white' : 'text-slate-400'}`}
              >
                Both
              </button>
              <button 
                onClick={() => setExpandedView('store')}
                className={`px-2 py-1 rounded text-xs ${expandedView === 'store' ? 'bg-emerald-600 text-white' : 'text-slate-400'}`}
              >
                Store
              </button>
            </div>
          )}

          <Button variant="outline" size="sm" className="border-slate-600">
            <Settings className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Fuel Pumps Section */}
        <div className={`${!convenienceStoreEnabled || expandedView === 'pumps' ? 'flex-1' : expandedView === 'store' ? 'hidden' : 'w-1/2'} border-r border-slate-700 flex flex-col transition-all`}>
          {/* Pump Stats */}
          <div className="grid grid-cols-4 gap-2 p-3 border-b border-slate-700">
            <div className="bg-slate-800/50 rounded-lg p-2 text-center">
              <p className="text-lg font-bold text-emerald-400">{activePumps}</p>
              <p className="text-[10px] text-slate-400">Active</p>
            </div>
            <div className="bg-slate-800/50 rounded-lg p-2 text-center">
              <p className="text-lg font-bold text-blue-400">{completedPumps}</p>
              <p className="text-[10px] text-slate-400">Complete</p>
            </div>
            <div className="bg-slate-800/50 rounded-lg p-2 text-center">
              <p className="text-lg font-bold text-white">${pumps.reduce((s, p) => s + p.currentAmount, 0).toFixed(0)}</p>
              <p className="text-[10px] text-slate-400">Current</p>
            </div>
            <div className="bg-slate-800/50 rounded-lg p-2 text-center">
              <p className="text-lg font-bold text-emerald-400">$2,847</p>
              <p className="text-[10px] text-slate-400">Today</p>
            </div>
          </div>

          {/* Pump Grid */}
          <div className="flex-1 p-3 overflow-auto">
            <div className="grid grid-cols-4 gap-3">
              {pumps.map((pump) => (
                <motion.div
                  key={pump.id}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleSelectPump(pump)}
                  className={`relative p-3 rounded-xl border-2 cursor-pointer transition-all
                    ${pump.status === PUMP_STATUS.IDLE ? 'border-slate-600 bg-slate-800/50' : ''}
                    ${pump.status === PUMP_STATUS.AUTHORIZED ? 'border-yellow-500 bg-yellow-500/10' : ''}
                    ${pump.status === PUMP_STATUS.PUMPING ? 'border-emerald-500 bg-emerald-500/10 animate-pulse' : ''}
                    ${pump.status === PUMP_STATUS.COMPLETE ? 'border-blue-500 bg-blue-500/10' : ''}
                    ${pump.status === PUMP_STATUS.OFFLINE ? 'border-red-500 bg-red-500/10 opacity-50' : ''}
                  `}
                >
                  <div className="absolute -top-2 -left-2 h-6 w-6 rounded-full bg-slate-700 border border-slate-600 flex items-center justify-center">
                    <span className="text-xs font-bold">{pump.id}</span>
                  </div>
                  
                  {pump.selectedGrade && (
                    <div className="absolute -top-2 -right-2">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium text-white ${FUEL_GRADES.find(g => g.id === pump.selectedGrade)?.color}`}>
                        {FUEL_GRADES.find(g => g.id === pump.selectedGrade)?.name}
                      </span>
                    </div>
                  )}

                  <div className="text-center mt-2">
                    <p className="text-xl font-bold font-mono">${pump.currentAmount.toFixed(2)}</p>
                    <p className="text-xs text-slate-400">{pump.currentGallons.toFixed(3)} gal</p>
                    {pump.presetAmount && (
                      <div className="mt-2">
                        <div className="w-full bg-slate-700 rounded-full h-1">
                          <div 
                            className="bg-emerald-500 h-1 rounded-full transition-all"
                            style={{ width: `${(pump.currentAmount / pump.presetAmount) * 100}%` }}
                          />
                        </div>
                        <p className="text-[10px] text-slate-500 mt-1">Preset: ${pump.presetAmount}</p>
                      </div>
                    )}
                  </div>

                  {pump.status === PUMP_STATUS.COMPLETE && (
                    <Button 
                      size="sm" 
                      className="w-full mt-2 bg-blue-600 hover:bg-blue-700 text-xs h-7"
                    >
                      + Add to Cart
                    </Button>
                  )}

                  {pump.status === PUMP_STATUS.IDLE && (
                    <p className="text-center text-xs text-slate-500 mt-2">Tap to Prepay</p>
                  )}
                </motion.div>
              ))}
            </div>
          </div>

          {/* Fuel Prices */}
          <div className="p-3 border-t border-slate-700">
            <div className="flex items-center justify-between gap-2">
              {FUEL_GRADES.map((grade) => (
                <div key={grade.id} className="flex items-center gap-1.5 bg-slate-800/50 rounded-lg px-2 py-1.5">
                  <div className={`h-2 w-2 rounded-full ${grade.color}`}></div>
                  <span className="text-xs text-slate-400">{grade.name}</span>
                  <span className="text-xs font-bold">${grade.price.toFixed(3)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Convenience Store Section */}
        {convenienceStoreEnabled && expandedView !== 'pumps' && (
          <div className={`${expandedView === 'store' ? 'flex-1' : 'w-1/2'} flex flex-col transition-all`}>
            {/* Store Header */}
            <div className="p-3 border-b border-slate-700">
              <div className="flex items-center gap-2 mb-3">
                <Store className="h-5 w-5 text-emerald-400" />
                <h2 className="font-bold">Convenience Store</h2>
              </div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input 
                  placeholder="Search products..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 bg-slate-800 border-slate-600"
                />
              </div>
            </div>

            {/* Categories */}
            <div className="flex gap-1 px-3 py-2 border-b border-slate-700 overflow-x-auto">
              {STORE_CATEGORIES.map((cat) => {
                const Icon = cat.icon;
                return (
                  <button
                    key={cat.id}
                    onClick={() => setSelectedCategory(cat.id)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs whitespace-nowrap transition-colors
                      ${selectedCategory === cat.id ? 'bg-emerald-600 text-white' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'}`}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    {cat.name}
                  </button>
                );
              })}
            </div>

            {/* Products Grid */}
            <div className="flex-1 p-3 overflow-auto">
              <div className="grid grid-cols-3 gap-2">
                {filteredProducts.map((product) => (
                  <motion.button
                    key={product.id}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handleAddToCart(product)}
                    className="bg-slate-800 hover:bg-slate-700 rounded-xl p-3 text-left transition-colors relative"
                  >
                    {product.ageRestricted && (
                      <div className="absolute top-1 right-1 bg-yellow-500 text-yellow-900 text-[8px] font-bold px-1 rounded">
                        21+
                      </div>
                    )}
                    <div className="text-2xl mb-1">{product.icon}</div>
                    <p className="text-sm font-medium text-white truncate">{product.name}</p>
                    <p className="text-sm font-bold text-emerald-400">${product.price.toFixed(2)}</p>
                  </motion.button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Cart / Checkout */}
        <div className="w-80 bg-slate-800/30 border-l border-slate-700 flex flex-col">
          <div className="p-3 border-b border-slate-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ShoppingCart className="h-5 w-5 text-emerald-400" />
                <h2 className="font-bold">Cart</h2>
              </div>
              <span className="text-sm text-slate-400">{cart.length} items</span>
            </div>
          </div>

          {/* Cart Items */}
          <div className="flex-1 overflow-auto p-3 space-y-2">
            {cart.length === 0 ? (
              <div className="text-center text-slate-500 py-8">
                <ShoppingCart className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p className="text-sm">Cart is empty</p>
                <p className="text-xs">Add fuel or store items</p>
              </div>
            ) : (
              cart.map((item) => (
                <div key={item.id} className="bg-slate-800 rounded-lg p-2">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-white">{item.name}</p>
                      {item.isFuel && (
                        <p className="text-xs text-slate-400">{item.gallons?.toFixed(3)} gallons</p>
                      )}
                      <p className="text-sm font-bold text-emerald-400">${(item.price * item.quantity).toFixed(2)}</p>
                    </div>
                    {!item.isFuel ? (
                      <div className="flex items-center gap-1">
                        <button 
                          onClick={() => updateCartQuantity(item.id, -1)}
                          className="h-6 w-6 rounded bg-slate-700 flex items-center justify-center hover:bg-slate-600"
                        >
                          <Minus className="h-3 w-3" />
                        </button>
                        <span className="w-6 text-center text-sm">{item.quantity}</span>
                        <button 
                          onClick={() => updateCartQuantity(item.id, 1)}
                          className="h-6 w-6 rounded bg-slate-700 flex items-center justify-center hover:bg-slate-600"
                        >
                          <Plus className="h-3 w-3" />
                        </button>
                      </div>
                    ) : (
                      <button 
                        onClick={() => removeFromCart(item.id)}
                        className="text-red-400 hover:text-red-300"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Totals & Checkout */}
          <div className="p-3 border-t border-slate-700 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-slate-400">Subtotal</span>
              <span>${cartTotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-400">Tax (8.25%)</span>
              <span>${taxAmount.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-lg font-bold border-t border-slate-600 pt-2">
              <span>Total</span>
              <span className="text-emerald-400">${grandTotal.toFixed(2)}</span>
            </div>

            <div className="grid grid-cols-2 gap-2 pt-2">
              <Button className="bg-emerald-600 hover:bg-emerald-700">
                <Banknote className="h-4 w-4 mr-1" />
                Cash
              </Button>
              <Button className="bg-blue-600 hover:bg-blue-700">
                <CreditCard className="h-4 w-4 mr-1" />
                Card
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Dialogs */}
      <PrepayDialog
        open={showPrepayDialog}
        onClose={() => setShowPrepayDialog(false)}
        pump={selectedPump}
        onAuthorize={handleAuthorize}
      />

      <AgeVerificationDialog
        open={!!pendingAgeVerification}
        onClose={() => setPendingAgeVerification(null)}
        onConfirm={handleAgeVerified}
        product={pendingAgeVerification}
      />
    </div>
  );
};

export default GasStationCStorePOS;
